/**
 * `/teams` — the Ivanti Employee Teams this person looks after.
 *
 * An admin sees every team; a team manager sees their own (the server decides,
 * by Employee RecId). One row per team: name, manager, team email, member
 * count, the Service Desk flag and its problem badges, so a leaver still on a
 * team is visible without opening it (2 Oct). A count-only team ("End Users")
 * reads "Counting members…" until the server's background count lands; the
 * list re-reads every 20 s meanwhile (`useTeams`).
 */

import { Badge, DataGrid, type DataGridColumn, Typography } from "@norican/react";
import { useMemo } from "react";
import { Link } from "react-router";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { TeamSummary } from "@/lib/api";

import { useTeams } from "./api";
import { memberCountState } from "./lib/team-count";
import { TeamProblemBadges } from "./team-problems";

function NotSet() {
  const { t } = useT();
  return (
    <Typography variant="caption" tone="muted">
      {t("teams.notSet")}
    </Typography>
  );
}

export function TeamsPage() {
  const { t } = useT();
  useDocumentTitle(t("teams.title"));

  const query = useTeams();
  const items = query.data?.items ?? [];

  const columns = useMemo<DataGridColumn<TeamSummary>[]>(
    () => [
      {
        id: "name",
        header: t("teams.column.name"),
        width: 220,
        cell: (row) => (
          <Link
            to={`/teams/${encodeURIComponent(row.id)}`}
            className="text-accent-fg-subtle hover:text-accent-hover font-medium"
          >
            {row.name}
          </Link>
        ),
      },
      {
        id: "manager",
        header: t("teams.column.manager"),
        width: 180,
        cell: (row) => (row.manager_name ? row.manager_name : <NotSet />),
      },
      {
        id: "email",
        header: t("teams.column.email"),
        width: 230,
        cell: (row) => (row.email ? <span className="break-all">{row.email}</span> : <NotSet />),
      },
      {
        id: "members",
        header: t("teams.column.members"),
        width: 150,
        align: "end",
        numeric: true,
        cell: (row) => {
          const state = memberCountState(row);
          return state.kind === "pending" ? (
            <Typography variant="caption" tone="muted">
              {t("teams.countingMembers")}
            </Typography>
          ) : (
            t("teams.memberCount", { count: state.count })
          );
        },
      },
      {
        id: "serviceDesk",
        header: t("teams.column.serviceDesk"),
        width: 130,
        cell: (row) =>
          row.is_service_desk ? (
            <Badge tone="info" variant="subtle" size="sm">
              {t("teams.serviceDesk")}
            </Badge>
          ) : null,
      },
      {
        id: "problems",
        header: t("teams.column.problems"),
        width: 280,
        cell: (row) => <TeamProblemBadges problems={row.problems ?? []} />,
      },
    ],
    [t],
  );

  return (
    <PageContainer
      title={t("teams.title")}
      subtitle={t("teams.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("teams.title") }]} />}
      width="wide"
    >
      {query.isLoading ? (
        <LoadingRows rows={5} />
      ) : query.error != null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyStateBlock title={t("teams.empty.title")} description={t("teams.empty.hint")} />
      ) : (
        <DataGrid
          aria-label={t("teams.tableLabel")}
          columns={columns}
          data={items}
          getRowId={(row) => row.id}
          tableLayout="fixed"
        />
      )}
    </PageContainer>
  );
}
